import React, { useState } from 'react';
import { View, Text, TextInput, Platform, Pressable, StyleProp, ViewStyle, Modal } from 'react-native';
import DateTimePicker, { DateTimePickerEvent } from '@react-native-community/datetimepicker';
import { formatDate } from 'shared-utils';
import { Button } from './button';
import { FormLabel } from './form';
import { cn } from '@/lib/utils';

interface DateTimeInputProps {
  label: string;
  value: Date | null;
  onChange: (date: Date | undefined) => void;
  mode?: 'date' | 'time' | 'datetime';
  style?: StyleProp<ViewStyle>;
  /**
   * Where the spinner opens when nothing has been picked yet. Without one it opened at the
   * current time, and on Android dismissing it counted as a selection — which is why the office
   * sees unavailability starting at 08:09 or 22:34 rather than at a time anyone chose.
   */
  defaultValue?: Date;
}

const DateTimeInput: React.FC<DateTimeInputProps> = ({ label, value, onChange, mode = 'date', style, defaultValue }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  // What the wheel is showing while the iOS modal is open, before Confirm accepts it.
  const [draft, setDraft] = useState<Date | null>(null);
  const isAndroid = Platform.OS === 'android';
  const pickerValue = draft || value || defaultValue || new Date();

  // Drop the seconds the picker inherited from "now" so a 09:00 booking is really 09:00:00.
  const withoutSeconds = (date: Date) => {
    const picked = new Date(date);
    picked.setSeconds(0, 0);
    return picked;
  };

  const handleDateChange = (event: DateTimePickerEvent, selectedDate?: Date) => {
    // On Android, hide the picker immediately after selection
    if (isAndroid) {
      setShowDatePicker(false);
    }

    // Android fires this on dismiss too, carrying whatever the wheel happened to be showing.
    if (event.type === 'dismissed') {
      return;
    }

    if (!selectedDate) {
      return;
    }

    // Android has no Confirm button, so the spin is the choice. On iOS it is only a draft until
    // Confirm: the wheel fires this as it moves, and a value that lands mid-scroll is not one
    // anyone picked.
    if (isAndroid) {
      onChange(withoutSeconds(selectedDate));
    } else {
      setDraft(selectedDate);
    }
  };

  const closePicker = () => {
    setShowDatePicker(false);
    setDraft(null);
  };

  /**
   * Confirm has to commit what the wheel is showing, not just close the modal.
   *
   * It used to only close, so the value was set purely as a side effect of scrolling: open the
   * picker, read 09:00, press Confirm, and the field stayed empty with nothing to say why.
   */
  const confirmPicker = () => {
    onChange(withoutSeconds(pickerValue));
    closePicker();
  };

  const getFormattedValue = () => {
    if (!value) return '';
    switch (mode) {
      case 'time':
        return formatDate(value, 'HH:mm');
      case 'datetime':
        return formatDate(value, 'dd/MM/yyyy HH:mm');
      case 'date':
      default:
        return formatDate(value, 'dd/MM/yyyy');
    }
  };

  const openPicker = () => {
    setDraft(null);
    setShowDatePicker(true);
  };

  return (
    <View style={[{ padding: 0, margin: 0 }, style]}>
      {label && <FormLabel className="text-base">{label}</FormLabel>}
      <Pressable style={{ marginTop: 0 }} onPress={openPicker}>
        <TextInput
          className={cn(
            "flex h-[44px] w-full items-center justify-center rounded-lg border bg-background px-4 text-base text-foreground",
            Platform.OS === "ios" ? "leading-[0px] pt-[2px]" : "leading-normal",
            !value && "text-muted-foreground"
          )}
          style={{ borderColor: '#ddd', marginTop: 0 }}
          onFocus={openPicker}
          onPressIn={openPicker}
          placeholder={mode === 'date' ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:MM'}
          placeholderTextColor="#9ca3af"
          value={getFormattedValue()}
          editable={false}
          textAlignVertical="center"
        />
      </Pressable>

      {/* For Android, render the picker directly when visible */}
      {isAndroid && showDatePicker && (
        <DateTimePicker
          {...({
            testID: 'dateTimePicker',
            value: pickerValue,
            mode,
            is24Hour: true,
            onChange: handleDateChange,
          } as React.ComponentProps<typeof DateTimePicker>)}
        />
      )}

      {/* For iOS, use modal approach */}
      {!isAndroid && (
        <Modal
          animationType="fade"
          transparent={true}
          visible={showDatePicker}
          onRequestClose={closePicker}
        >
          <Pressable
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
            onPress={closePicker}
          >
            <View
              style={{
                backgroundColor: 'white',
                borderRadius: 12,
                padding: 20,
                width: '90%',
                maxWidth: 400,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.25,
                shadowRadius: 4,
                elevation: 5,
              }}
              onStartShouldSetResponder={() => true}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 15, textAlign: 'center' }}>
                {mode === 'date' ? 'Select Date' : mode === 'time' ? 'Select Time' : 'Select Date & Time'}
              </Text>

              <DateTimePicker
                testID="dateTimePicker"
                value={pickerValue}
                mode={mode}
                display="spinner"
                onChange={handleDateChange}
                style={{ width: '100%' }}
              />

              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15 }}>
                <Button
                  variant="outline"
                  onPress={closePicker}
                  style={{ flex: 1, marginRight: 8 }}
                >
                  <Text style={{ fontWeight: '600' }}>Cancel</Text>
                </Button>
                <Button
                  onPress={confirmPicker}
                  style={{ flex: 1, marginLeft: 8 }}
                >
                  <Text style={{ color: "#FFF000", fontWeight: '600' }}>Confirm</Text>
                </Button>
              </View>
            </View>
          </Pressable>
        </Modal>
      )}
    </View>
  );
};

export {
  DateTimeInput
}
